import React, { useCallback, useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, AlertTriangleIcon, CheckIcon, FacebookIcon, InstagramIcon, TwitterIcon, FileTextIcon, LayoutTemplateIcon, PaletteIcon, Share2Icon, TagsIcon, UsersIcon } from 'lucide-react';
import { InternalTeamsConfig } from './notice/InternalTeamsConfig';
import { EmailIdentityConfig } from './notice/EmailIdentityConfig';
import { useLanguage } from '../contexts/LanguageContext';
import type { ChannelKey, Localized } from '../types/notice';
import { CHANNEL_ICONS, CHANNEL_LABELS } from '../data/channels';
import { IconTooltip } from './IconTooltip';
import { CategoryFormDialog } from './notice/CategoryFormDialog';
import { ConfirmDeleteDialog } from './notice/ConfirmDeleteDialog';
import { CATEGORIES, getCategoryIcon, getCategoryLabel, getTemplateCategory, getTemplateChannels, type NoticeTemplate } from '../data/communications';
export type ConfigSection = 'categories' | 'equipes' | 'reseaux' | 'modeles' | 'identite';
type Section = ConfigSection;
export interface Categorie {
  id: string;
  titre: Localized;
  description: Localized;
  urgence: boolean;
  defaut: boolean;
  statut: 'active' | 'brouillon' | 'inactive';
}
export const MOCK_CATEGORIES: Categorie[] = [{
  id: '1',
  titre: {
    fr: 'Urgence',
    en: 'Emergency'
  },
  description: {
    fr: 'Avis critiques nécessitant une action immédiate',
    en: 'Critical notices requiring immediate action'
  },
  urgence: true,
  defaut: false,
  statut: 'active'
}, {
  id: '2',
  titre: {
    fr: 'Eau potable',
    en: 'Drinking water'
  },
  description: {
    fr: "Avis d'ébullition et coupures d'eau",
    en: 'Boil advisories and water shutdowns'
  },
  urgence: true,
  defaut: false,
  statut: 'active'
}, {
  id: '3',
  titre: {
    fr: 'Travaux',
    en: 'Public works'
  },
  description: {
    fr: 'Fermetures de rues et chantiers',
    en: 'Street closures and work sites'
  },
  urgence: false,
  defaut: false,
  statut: 'active'
}, {
  id: '4',
  titre: {
    fr: 'Information générale',
    en: 'General information'
  },
  description: {
    fr: 'Nouvelles et mises à jour de la ville',
    en: 'City news and updates'
  },
  urgence: false,
  defaut: true,
  statut: 'active'
}];
interface CommunicationsConfigurerProps {
  categories: Categorie[];
  onCategoriesChange: (categories: Categorie[]) => void;
  templates: NoticeTemplate[];
  onCreateTemplate: () => void;
  onEditTemplate: (template: NoticeTemplate) => void;
  onDeleteTemplate: (templateId: string) => void;
  /** Section shown first — e.g. back on "Modèles" after editing a template. */
  initialSection?: Section;
}
export function CommunicationsConfigurer({
  categories,
  onCategoriesChange,
  templates,
  onCreateTemplate,
  onEditTemplate,
  onDeleteTemplate,
  initialSection = 'categories'
}: CommunicationsConfigurerProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [activeSection, setActiveSection] = useState<Section>(initialSection);
  /** Template awaiting a delete confirmation, shown inline on its row. */
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  /** Category form: closed, or open on a category (null = creating). */
  const [categoryForm, setCategoryForm] = useState<{
    open: false;
  } | {
    open: true;
    category: Categorie | null;
  }>({
    open: false
  });
  const [categoryToDelete, setCategoryToDelete] = useState<Categorie | null>(null);
  const closeCategoryForm = useCallback(() => setCategoryForm({
    open: false
  }), []);
  const cancelCategoryDelete = useCallback(() => setCategoryToDelete(null), []);
  const saveCategory = (saved: Categorie) => {
    const exists = categories.some(c => c.id === saved.id);
    const next = exists ? categories.map(c => c.id === saved.id ? saved : c) : [...categories, saved];
    // Only one category can be the default one.
    onCategoriesChange(saved.defaut ? next.map(c => c.id === saved.id ? c : {
      ...c,
      defaut: false
    }) : next);
    setCategoryForm({
      open: false
    });
  };
  const confirmCategoryDelete = () => {
    if (!categoryToDelete) return;
    onCategoriesChange(categories.filter(c => c.id !== categoryToDelete.id));
    setCategoryToDelete(null);
  };
  const statutLabel = (statut: Categorie['statut']) => statut === 'active' ? t('Active', 'Active') : statut === 'brouillon' ? t('Brouillon', 'Draft') : t('Inactive', 'Inactive');
  const renderCategories = () => <div className="space-y-6" data-id="element-1660" data-mp="yh5bd94atsqc">
      <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-1661" data-mp="jm7ga8hrmp5k">
        <div data-id="element-1662" data-mp="jobjeo7ubk4s">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1663" data-mp="ol227jb14ttw">
            {t("Catégories d'avis", 'Notice categories')}
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1664" data-mp="34b4zjaovgd7z">
            {t("Gérez les catégories disponibles lors de la création d'un avis.", 'Manage the categories available when creating a notice.')}
          </p>
        </div>
        <button type="button" onClick={() => setCategoryForm({
        open: true,
        category: null
      })} className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors flex items-center gap-2" data-id="element-1665" data-mp="1metj7v9ru7cd">
          <PlusIcon className="w-4 h-4" data-id="element-1666" data-mp="1wamqp08mvana" /> {t('Nouvelle catégorie', 'New category')}
        </button>
      </div>

      {categories.length === 0 ? <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center" data-id="element-1667" data-mp="13ov1eng68zvj">
          <div className="mx-auto w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-[#4F46E5] mb-3" data-id="element-1668" data-mp="1hfqao0ul1izn">
            <TagsIcon className="w-6 h-6" data-id="element-1669" data-mp="1cn707cr6z3z2" />
          </div>
          <p className="font-semibold text-slate-900" data-id="element-1670" data-mp="oynqcsshuj4v">
            {t('Aucune catégorie', 'No categories')}
          </p>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1671" data-mp="kwj1s8gwkcp3">
            {t('Créez une première catégorie pour classer vos avis.', 'Create a first category to organize your notices.')}
          </p>
        </div> : <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-1672" data-mp="2ts5a5dko891i">
        <div className="overflow-x-auto" data-id="element-1673" data-mp="2yr8ib6vy6vdp">
          <table className="w-full text-left border-collapse" data-id="element-1674" data-mp="2yq6vk55kkkg5">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1675" data-mp="213hohgw0fhnf">
              <tr data-id="element-1676" data-mp="215klikvkyb1n">
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-1677" data-mp="262kscy097htt">
                  {t('Titre et description', 'Title & description')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center" data-id="element-1678" data-mp="2b40c8mqp3iam">
                  {t('Urgence', 'Emergency')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center" data-id="element-1679" data-mp="3mix4e5i6enm7">
                  {t('Par défaut', 'Default')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-1680" data-mp="1fs0vyyp57qi5">
                  {t('Statut', 'Status')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-right" data-id="element-1681" data-mp="161z37tluhjww">
                  {t('Actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1682" data-mp="w5clvesq9azm">
              {categories.map(cat => <tr key={cat.id} onDoubleClick={() => setCategoryForm({
              open: true,
              category: cat
            })} className="hover:bg-slate-50 transition-colors" data-id="element-1683" data-mp="s26j4h4tq1xw">
                  <td className="px-4 py-3" data-id="element-1684" data-mp="i6mqq4pgxldw">
                    <div className="font-semibold text-slate-900 text-sm" data-id="element-1685" data-mp="2hifo42g64sr5">
                      {cat.titre[lang]}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5" data-id="element-1686" data-mp="2cjcleonnuymc">
                      {cat.description[lang]}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center" data-id="element-1687" data-mp="1qctj9lo088c9">
                    {cat.urgence && <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-red-100 text-red-600" data-id="element-1688" data-mp="1bofirhe28vyv">
                        <AlertTriangleIcon className="w-3 h-3" data-id="element-1689" data-mp="1glfotsawtypz" />
                      </span>}
                  </td>
                  <td className="px-4 py-3 text-center" data-id="element-1690" data-mp="12pbywb72kcjb">
                    {cat.defaut && <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-emerald-100 text-emerald-600" data-id="element-1691" data-mp="17jzgrr7ki8ox">
                        <CheckIcon className="w-3 h-3" data-id="element-1692" data-mp="xnmp0yvl24wt" />
                      </span>}
                  </td>
                  <td className="px-4 py-3" data-id="element-1693" data-mp="2n3vpc6vzg7jq">
                    <span className={`inline-flex px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border ${cat.statut === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : cat.statut === 'brouillon' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`} data-id="element-1694" data-mp="2n0zvatn9a30k">
                      {statutLabel(cat.statut)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-1695" data-mp="lcc2lhi34bgo">
                    <div className="flex items-center justify-end gap-2" data-id="element-1696" data-mp="z4a6ndwxvh5z">
                      <button type="button" onClick={() => setCategoryForm({
                    open: true,
                    category: cat
                  })} className="p-1.5 text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 rounded transition-colors" aria-label={`${t('Modifier', 'Edit')} ${cat.titre[lang]}`} data-id="element-1697" data-mp="v3ghyibo9yc1">
                        <PencilIcon className="w-4 h-4" data-id="element-1698" data-mp="3awffk4ou4ihq" />
                      </button>
                      <button type="button" onClick={() => setCategoryToDelete(cat)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" aria-label={`${t('Supprimer', 'Delete')} ${cat.titre[lang]}`} data-id="element-1699" data-mp="35xm38flaaxj5">
                        <TrashIcon className="w-4 h-4" data-id="element-1700" data-mp="vpukqn2e1b4u" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>}

      <CategoryFormDialog open={categoryForm.open} category={categoryForm.open ? categoryForm.category : null} onClose={closeCategoryForm} onSave={saveCategory} data-id="element-1701" data-mp="1ni207k65qhhn" />

      <ConfirmDeleteDialog open={Boolean(categoryToDelete)} title={t('Supprimer cette catégorie ?', 'Delete this category?')} message={categoryToDelete && <>
              {t('La catégorie ', 'The category ')}
              <span className="font-semibold text-slate-700" data-id="element-1703" data-mp="3p2wb5wp18szc">
                « {categoryToDelete.titre[lang]} »
              </span>
              {t(' ne sera plus proposée à la création d’un avis. Les avis déjà envoyés ne sont pas modifiés.', ' will no longer be offered when creating a notice. Notices already sent are not affected.')}
            </>} onCancel={cancelCategoryDelete} onConfirm={confirmCategoryDelete} data-id="element-1702" data-mp="1elugm8yhkytp" />
    </div>;
  const renderReseaux = () => <div className="space-y-6" data-id="element-1704" data-mp="ll4gubnfbwxz">
      <div data-id="element-1705" data-mp="llxgksnytm6m">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1706" data-mp="lle7m1gam84q">
          {t('Réseaux sociaux', 'Social media')}
        </h3>
        <p className="text-sm text-slate-500 mt-1" data-id="element-1707" data-mp="pok8suojt94c">
          {t('Connectez les comptes officiels pour la publication automatisée.', 'Connect the official accounts for automated publishing.')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1708" data-mp="2vn0deo7f4ok0">
        {/* Facebook */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col shadow-sm" data-id="element-1709" data-mp="2qp79y2xi28nf">
          <div className="flex items-start justify-between mb-4" data-id="element-1710" data-mp="35e3j63balrpm">
            <div className="flex items-center gap-3" data-id="element-1711" data-mp="35femk20tsxg7">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-1712" data-mp="30m3a9ld8vogw">
                <FacebookIcon className="w-5 h-5" data-id="element-1713" data-mp="3ecownk1wqgjj" />
              </div>
              <div data-id="element-1714" data-mp="3adg60o25yys7">
                <h4 className="font-semibold text-slate-900" data-id="element-1715" data-mp="3ac4zighc3cxe">Facebook</h4>
                <p className="text-xs text-emerald-600 font-medium flex items-center gap-1 mt-0.5" data-id="element-1716" data-mp="3ocpl1kemvmul">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1717" data-mp="3jd3696j5pyjo" />{' '}
                  {t('Connecté', 'Connected')}
                </p>
              </div>
            </div>
            <button className="text-xs font-semibold text-slate-500 hover:text-slate-700" data-id="element-1718" data-mp="1ozv5yzcewuqn">
              {t('Déconnecter', 'Disconnect')}
            </button>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 mt-auto" data-id="element-1719" data-mp="4jnerwbmr7wb">
            <span className="font-medium text-slate-900" data-id="element-1720" data-mp="9igoddnsqer7">
              {t('Page :', 'Page:')}
            </span>{' '}
            Ville de Windsor
          </div>
        </div>

        {/* X (Twitter) */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col shadow-sm" data-id="element-1721" data-mp="3vtm0e243xkq8">
          <div className="flex items-start justify-between mb-4" data-id="element-1722" data-mp="3hyjzxui14nkt">
            <div className="flex items-center gap-3" data-id="element-1723" data-mp="3mwv9030i2u1z">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-900" data-id="element-1724" data-mp="37y9l65gcj83j">
                <TwitterIcon className="w-5 h-5" data-id="element-1725" data-mp="3cwb48qdrv7f0" />
              </div>
              <div data-id="element-1726" data-mp="166n0smqymbg1">
                <h4 className="font-semibold text-slate-900" data-id="element-1727" data-mp="1664zhbpfx719">X (Twitter)</h4>
                <p className="text-xs text-emerald-600 font-medium flex items-center gap-1 mt-0.5" data-id="element-1728" data-mp="1b5r817ubpz3q">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1729" data-mp="378vxihxvi5b4" />{' '}
                  {t('Connecté', 'Connected')}
                </p>
              </div>
            </div>
            <button className="text-xs font-semibold text-slate-500 hover:text-slate-700" data-id="element-1730" data-mp="335g5y00nuv5a">
              {t('Déconnecter', 'Disconnect')}
            </button>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 mt-auto" data-id="element-1731" data-mp="2t8lbbs7idewa">
            <span className="font-medium text-slate-900" data-id="element-1732" data-mp="2oatgoqfhbeoe">
              {t('Compte :', 'Account:')}
            </span>{' '}
            @villewindsor
          </div>
        </div>

        {/* Instagram */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col shadow-sm" data-id="element-1733" data-mp="2fi7zvfveri27">
          <div className="flex items-start justify-between mb-4" data-id="element-1734" data-mp="2ak6hkx5t3mrs">
            <div className="flex items-center gap-3" data-id="element-1735" data-mp="25js8dbsgy6s6">
              <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center text-pink-600" data-id="element-1736" data-mp="25lmsinvx1uv7">
                <InstagramIcon className="w-5 h-5" data-id="element-1737" data-mp="1qgirhpgw2r53" />
              </div>
              <div data-id="element-1738" data-mp="1qg9140rxmhyv">
                <h4 className="font-semibold text-slate-900" data-id="element-1739" data-mp="1qiv3zn3vo9ar">Instagram</h4>
                <p className="text-xs text-slate-400 font-medium flex items-center gap-1 mt-0.5" data-id="element-1740" data-mp="1vdqzix8bpesm">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300" data-id="element-1741" data-mp="20fgbcf3ddwhr" />{' '}
                  {t('Non connecté', 'Not connected')}
                </p>
              </div>
            </div>
          </div>
          <button className="mt-auto w-full py-2.5 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors" data-id="element-1742" data-mp="17ru672ehujss">
            {t('Connecter le compte', 'Connect the account')}
          </button>
        </div>
      </div>
    </div>;
  const renderModeles = () => <div className="space-y-6" data-id="element-1743" data-mp="1mg8cxof16sem">
      <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-1744" data-mp="ozrnzmm7evgf">
        <div data-id="element-1745" data-mp="oxf6fakcyn3w">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1746" data-mp="tymg5f629wd7">
            {t("Modèles d'avis", 'Notice templates')}
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1747" data-mp="lx50rvw258rk">
            {t("Configurez des modèles prédéfinis pour accélérer la création d'avis.", 'Set up predefined templates to speed up notice creation.')}
          </p>
        </div>
        <button type="button" onClick={onCreateTemplate} className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors flex items-center gap-2" data-id="element-1748" data-mp="qon1qjy2z9cj">
          <PlusIcon className="w-4 h-4" data-id="element-1749" data-mp="3u6f0qiamwtk8" /> {t('Nouveau modèle', 'New template')}
        </button>
      </div>

      {templates.length === 0 ? <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center" data-id="element-1750" data-mp="cv5mvcgcaksu">
          <div className="mx-auto w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-[#4F46E5] mb-3" data-id="element-1751" data-mp="80i65zrkuuot">
            <LayoutTemplateIcon className="w-6 h-6" data-id="element-1752" data-mp="1jdkj8kugx248" />
          </div>
          <p className="font-semibold text-slate-900" data-id="element-1753" data-mp="1jfevlaroblwp">
            {t('Aucun modèle', 'No templates')}
          </p>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1754" data-mp="1vvwr1fog88sa">
            {t('Créez un modèle ici ou enregistrez un avis comme modèle depuis le formulaire.', 'Create a template here, or save a notice as a template from the composer.')}
          </p>
        </div> : <ul className="grid grid-cols-1 gap-3" data-id="element-1755" data-mp="29nl35iyp6x99">
          {templates.map(tpl => {
        const confirming = confirmDeleteId === tpl.id;
        const categoryId = getTemplateCategory(tpl);
        const CategoryIcon = getCategoryIcon(categoryId);
        const urgent = Boolean(CATEGORIES.find(c => c.value === categoryId)?.urgent);
        const channels = getTemplateChannels(tpl) as ChannelKey[];
        return <li key={tpl.id} onClick={() => !confirming && onEditTemplate(tpl)} className="bg-white rounded-xl border border-slate-200 px-5 py-4 flex items-center justify-between gap-4 hover:border-[#4F46E5]/50 hover:shadow-sm transition-all group cursor-pointer" data-id="element-1756" data-mp="2skcgzcvh0k5x">
                <div className="flex items-center gap-4 min-w-0" data-id="element-1757" data-mp="1yvwxf893onfx">
                  <IconTooltip label={getCategoryLabel(categoryId)[lang]} data-id="element-1758" data-mp="1yu3mhgrgfh24">
                    <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${urgent ? 'bg-red-50 text-red-600' : 'bg-indigo-50 text-[#4F46E5]'}`} data-id="element-1759" data-mp="1tywmok1b2wih">
                      <CategoryIcon className="w-5 h-5" aria-hidden="true" data-id="element-1760" data-mp="2hlwgnei9f8bf" />
                    </span>
                  </IconTooltip>
                  {/* Channels: up to 3 per row, 2 rows, vertically centered */}
                  <div className="grid grid-cols-3 gap-1 w-[68px] flex-shrink-0 content-center" data-id="element-1761" data-mp="168k7qp7n7zie">
                    {channels.map(c => {
                const Icon = CHANNEL_ICONS[c];
                return <IconTooltip key={c} label={CHANNEL_LABELS[c][lang]} data-id="element-1762" data-mp="3ctyfqmgsoimn">
                          <span className="w-5 h-5 flex items-center justify-center text-slate-400 hover:text-[#4F46E5] transition-colors" data-id="element-1763" data-mp="3hvnp7xodco8m">
                            <Icon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1764" data-mp="3rkxjnk6oqizw" />
                          </span>
                        </IconTooltip>;
              })}
                  </div>
                  <div className="min-w-0" data-id="element-1765" data-mp="4fop8pxx8wkk">
                    <div className="flex items-center gap-2 min-w-0" data-id="element-1766" data-mp="4en4tuv0mxq6">
                      <h4 className="font-semibold text-slate-900 truncate" data-id="element-1767" data-mp="9gcfvai8nbe9">
                        {tpl.name[lang]}
                      </h4>
                      {tpl.custom && <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[#4F46E5] bg-indigo-50 px-1.5 py-0.5 rounded" data-id="element-1768" data-mp="2b77gqgray0ru">
                          {t('Équipe', 'Team')}
                        </span>}
                    </div>
                    <p className="text-sm text-slate-500 truncate mt-0.5" data-id="element-1769" data-mp="t39i6rraw2w7">
                      {tpl.description[lang]}
                    </p>
                  </div>
                </div>

                {confirming ? <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()} data-id="element-1770" data-mp="11w33p6xcbyii">
                    <span className="text-sm text-slate-600 hidden sm:inline" data-id="element-1771" data-mp="1btqw3q7h4txi">
                      {t('Supprimer ce modèle ?', 'Delete this template?')}
                    </span>
                    <button type="button" onClick={() => setConfirmDeleteId(null)} className="px-3 py-1.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50" data-id="element-1772" data-mp="1gr985pgsfp9q">
                      {t('Annuler', 'Cancel')}
                    </button>
                    <button type="button" onClick={() => {
              onDeleteTemplate(tpl.id);
              setConfirmDeleteId(null);
            }} className="px-3 py-1.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700" data-id="element-1773" data-mp="3f3b02o86wv63">
                      {t('Supprimer', 'Delete')}
                    </button>
                  </div> : <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" data-id="element-1774" data-mp="3t3dhk9cl5mkt">
                    <button type="button" aria-label={t('Modifier', 'Edit')} onClick={e => {
              e.stopPropagation();
              onEditTemplate(tpl);
            }} className="p-2 text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 rounded-lg transition-colors" data-id="element-1775" data-mp="3p2joz2o77jll">
                      <PencilIcon className="w-4 h-4" data-id="element-1776" data-mp="2f2h4a9o94o6f" />
                    </button>
                    <button type="button" aria-label={t('Supprimer', 'Delete')} onClick={e => {
              e.stopPropagation();
              setConfirmDeleteId(tpl.id);
            }} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-1777" data-mp="212y3y31omh4o">
                      <TrashIcon className="w-4 h-4" data-id="element-1778" data-mp="1hfgw27oacmg5" />
                    </button>
                  </div>}
              </li>;
      })}
        </ul>}
    </div>;
  const nav: {
    id: Section;
    label: string;
    icon: typeof TagsIcon;
  }[] = [{
    id: 'categories',
    label: t('Catégories', 'Categories'),
    icon: TagsIcon
  }, {
    id: 'modeles',
    label: t('Modèles', 'Templates'),
    icon: LayoutTemplateIcon
  }, {
    id: 'equipes',
    label: t('Équipes internes', 'Internal teams'),
    icon: UsersIcon
  }, {
    id: 'reseaux',
    label: t('Réseaux sociaux', 'Social media'),
    icon: Share2Icon
  }, {
    id: 'identite',
    label: t('Identité', 'Identity'),
    icon: PaletteIcon
  }];
  return <div className="flex flex-col lg:flex-row gap-8 font-['Inter']" data-id="element-1779" data-mp="yqevvvu591ju">
      {/* Section switcher */}
      <div className="lg:w-64 flex-shrink-0" data-id="element-1780" data-mp="1cpo160gtab91">
        <nav aria-label={t('Sections de configuration', 'Configuration sections')} className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 lg:sticky lg:top-4 shadow-sm" data-id="element-1781" data-mp="1cocw7xbmqxgc">
          {nav.map(item => {
          const Icon = item.icon;
          const active = activeSection === item.id;
          return <button key={item.id} onClick={() => setActiveSection(item.id)} className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${active ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-1782" data-mp="1vg5n9m7528lf">
                <Icon className={`w-4 h-4 ${active ? 'text-white' : 'text-slate-400'}`} data-id="element-1783" data-mp="3uont66poc3tm" />
                {item.label}
              </button>;
        })}
        </nav>
      </div>

      {/* Content area */}
      <div className="flex-1 min-w-0" data-id="element-1784" data-mp="36xrsek38pya8">
        {activeSection === 'categories' && renderCategories()}
        {activeSection === 'equipes' && <InternalTeamsConfig data-id="element-1785" data-mp="3az3j6sxuopk0" />}
        {activeSection === 'reseaux' && renderReseaux()}
        {activeSection === 'modeles' && renderModeles()}
        {activeSection === 'identite' && <EmailIdentityConfig data-id="element-1786" data-mp="qafnl7obgwk4" />}
      </div>
    </div>;
}